import PropTypes from 'prop-types';
import { Box } from '@mui/material';
import KPICard from '../Common/KPICard';
import useAccountKPIs from '../../hooks/useAccountKPIs';
import useAccountHealthScores from '../../hooks/useAccountHealthScores';
import { getHealthScoreColor } from '../../utils/healthScoreUtils';
import { formatCurrency, formatDate, formatDateTime } from 'utils/utils';

// Shown when no score has been stored for the account yet, or the score could
// not be calculated (CR-6567 AC2).
const INSUFFICIENT_DATA = 'Insufficient Data';

const LP360KPIs = ({ accountId }) => {
	const {
		totalCommitments,
		contributionsToDate,
		activePipeline,
		lastInteraction,
	} = useAccountKPIs(accountId);
	const { healthScores, loading: healthScoreLoading } =
		useAccountHealthScores(accountId);

	const healthScoreClassification =
		healthScores?.classification || INSUFFICIENT_DATA;

	// AC2: show the as-of date and time. calculated_at is the full timestamp;
	// fall back to the date-only field for responses that predate it.
	const formatHealthScoreAsOf = (score) => {
		if (!score?.calculated_at && !score?.date)
			return 'No Health Score calculated';
		if (score.calculated_at)
			return `As of ${formatDateTime(score.calculated_at)}`;
		return `As of ${formatDate(score.date, true)}`;
	};

	const formatKPICurrency = (value) => {
		if (value === null || value === undefined) return '$0';
		return formatCurrency(value, 'USD', { compact: true });
	};

	const formatDaysSince = (days) => {
		if (days === null || days === undefined) return 'Unknown';
		return `${days} days`;
	};

	const formatInteractionDate = (dateString) => {
		if (!dateString) return 'No Last Interaction Date';
		return formatDate(dateString, true);
	};

	// All cards stretch to match the tallest card's height
	const cardWrapperSx = { flex: 1, display: 'flex', '& > *': { flex: 1 } };

	return (
		<Box display='flex' gap={3}>
			<Box sx={cardWrapperSx}>
				<KPICard
					label='Total Commitments'
					value={formatKPICurrency(totalCommitments.value)}
					loading={totalCommitments.loading}
				/>
			</Box>

			<Box sx={cardWrapperSx}>
				<KPICard
					label='Contributions to Date'
					value={formatKPICurrency(contributionsToDate.value)}
					loading={contributionsToDate.loading}
				/>
			</Box>

			<Box sx={cardWrapperSx}>
				<KPICard
					label='Total Active Pipeline'
					value={formatKPICurrency(activePipeline.value)}
					loading={activePipeline.loading}
				/>
			</Box>

			<Box sx={cardWrapperSx}>
				<KPICard
					label='Days Since Last Interaction'
					value={formatDaysSince(lastInteraction.daysSince)}
					sub={formatInteractionDate(lastInteraction.date)}
					loading={lastInteraction.loading}
				/>
			</Box>

			<Box sx={cardWrapperSx}>
				<KPICard
					label='Relationship Health Score'
					value={healthScoreClassification}
					valueColor={getHealthScoreColor(healthScoreClassification)}
					sub={formatHealthScoreAsOf(healthScores)}
					loading={healthScoreLoading}
				/>
			</Box>
		</Box>
	);
};

LP360KPIs.propTypes = {
	accountId: PropTypes.string.isRequired,
};

export default LP360KPIs;
